/**
 * MolecularSVGContext - Root context for molecular SVG rendering
 *
 * Provides workspace data (Layer 1: Data) to all child components.
 * This is the foundation that all other contexts and components build upon.
 */

import { createContext, useContext, useRef, useCallback, type ReactNode } from 'react';
import type { MolecularWorkspace, WorkspaceChange } from '@alchemiesolns/molecular-workspace';
import type { MolecularSVGContextValue } from '../types/contexts';

const MolecularSVGContext = createContext<MolecularSVGContextValue | null>(null);

export interface MolecularSVGProviderProps {
  /**
   * MolecularWorkspace instance (single source of truth)
   */
  workspace: MolecularWorkspace;

  /**
   * SVG viewport width in pixels (default: 800)
   */
  svgWidth?: number;

  /**
   * SVG viewport height in pixels (default: 600)
   */
  svgHeight?: number;

  /**
   * Padding around molecule bounds in SVG units (default: 10)
   * Added to accommodate text labels and decorations
   */
  padding?: number;

  /**
   * Horizontal alignment of molecule within viewport (default: 'center')
   * - 'left': Align to left edge
   * - 'center': Center horizontally
   * - 'right': Align to right edge
   */
  alignment?: 'left' | 'center' | 'right';

  /**
   * Callback when workspace changes
   * If not provided, component is read-only
   */
  onWorkspaceChange?: (workspace: MolecularWorkspace, change?: WorkspaceChange) => void;

  /**
   * Child components
   */
  children: ReactNode;
}

/**
 * MolecularSVGProvider - Root provider for molecular visualization
 *
 * Usage:
 * ```tsx
 * <MolecularSVGProvider workspace={workspace}>
 *   <SVGCanvas />
 * </MolecularSVGProvider>
 * ```
 */
export function MolecularSVGProvider({
  workspace,
  svgWidth = 800,
  svgHeight = 600,
  padding = 10,
  alignment = 'center',
  onWorkspaceChange,
  children,
}: MolecularSVGProviderProps) {
  // Track the latest change descriptor so downstream providers (SVGGenerationProvider)
  // can check what changed and skip expensive recomputation (PIPH-71)
  const latestChangeRef = useRef<WorkspaceChange | undefined>(undefined);

  const wrappedOnWorkspaceChange = useCallback(
    (ws: MolecularWorkspace, change?: WorkspaceChange) => {
      latestChangeRef.current = change;
      onWorkspaceChange?.(ws, change);
    },
    [onWorkspaceChange]
  );

  const value: MolecularSVGContextValue = {
    workspace,
    svgWidth,
    svgHeight,
    padding,
    alignment,
    onWorkspaceChange: onWorkspaceChange ? wrappedOnWorkspaceChange : undefined,
    latestChange: latestChangeRef,
  };

  return (
    <MolecularSVGContext.Provider value={value}>
      {children}
    </MolecularSVGContext.Provider>
  );
}

/**
 * Hook to access MolecularSVGContext
 *
 * @throws Error if used outside MolecularSVGProvider
 *
 * Usage:
 * ```tsx
 * const { workspace, svgWidth, svgHeight } = useMolecularSVG();
 * ```
 */
export function useMolecularSVG(): MolecularSVGContextValue {
  const context = useContext(MolecularSVGContext);

  if (!context) {
    throw new Error(
      'useMolecularSVG must be used within a MolecularSVGProvider. ' +
      'Wrap your component tree with <MolecularSVGProvider workspace={workspace}>...</MolecularSVGProvider>'
    );
  }

  return context;
}
